import { useEffect, useState } from "react";
import ModuleContainer from "./ModuleContainer";
import { supabase } from "../../lib/supabase";
import { getLearningLinks } from "../../lib/learningLinks";
import { colors } from "../../lib/designTokens";
import {
  aggregateBrandMentionsForFrequency,
  normalizeBrandMentionKey
} from "../../lib/brandMentionAggregation";
import React from "react";


type AnalystData = {
  total_mentions: number;
  discovery_mentions: number;
  recommendation_mentions: number;
  comparison_mentions: number;
  competitor_count: number;
  previous_recommendation_mentions?: number;
};

type ModuleStatus = "loading" | "ready" | "empty" | "error" | "unavailable";

/* ----------------------------
   INSIGHT LIBRARY (CORE)
---------------------------- */

const insightLibrary = {
  discoveryVsRecommendation: [
    {
      lead: "Strong discovery. Weak recommendation.",
      body: "You appear frequently in early-stage AI responses but are not consistently recommended.",
      action: "Strengthen positioning in high-intent queries by improving authority and comparative content."
    },
    {
      lead: "Visible, but not chosen.",
      body: "AI surfaces you in discovery, but you are not consistently selected in recommendations.",
      action: "Improve how your brand is framed in decision-stage queries."
    }
  ],

  comparisonPressure: [
    {
      lead: "You are in the race, but not winning.",
      body: "You appear in comparisons but are not consistently recommended.",
      action: "Strengthen competitive positioning and preference signals."
    },
    {
      lead: "Evaluation-stage weakness.",
      body: "AI includes you in comparisons, but favors competitors.",
      action: "Improve differentiation and authority in competitive queries."
    }
  ],

  strongPerformance: [
    {
      lead: "Strong recommendation performance.",
      body: "You are frequently recommended when you appear.",
      action: "Continue reinforcing authority signals to maintain dominance."
    }
  ],

  competitiveMarket: [
    {
      lead: "Highly competitive landscape.",
      body: "Multiple brands are consistently surfaced in AI responses.",
      action: "Focus on differentiation and authority to stand out."
    }
  ],

  balanced: [
    {
      lead: "Balanced performance.",
      body: "You maintain steady visibility across discovery and recommendation.",
      action: "Improve differentiation to strengthen preference."
    }
  ]
};

/* ----------------------------
   STABLE PICKER
---------------------------- */

function pickStable(arr: any[], key: string) {
  let hash = 0;
  for (let i = 0; i < key.length; i++) {
    hash = key.charCodeAt(i) + ((hash << 5) - hash);
  }
  return arr[Math.abs(hash) % arr.length];
}

/* ----------------------------
   COMPONENT
---------------------------- */

export default function AIAnalyst({ account, isPro, customerBrand, runId }: any) {
const [data, setData] = useState<AnalystData | null>(null);
const [competitiveData, setCompetitiveData] = useState<any[]>([]);
const [status, setStatus] = useState<ModuleStatus>("loading");
const [statusMessage, setStatusMessage] = useState("");
const [journeyData, setJourneyData] = useState<any[]>([]);
const [opportunityData, setOpportunityData] = useState<any[]>([]);

  useEffect(() => {
  if (!account) {
    setStatus("unavailable");
    setStatusMessage("This module is not available for this account yet.");
    setData(null);
    setCompetitiveData([]);
    return;
  }

  fetchAnalysis();
  fetchJourneyData();
  fetchOpportunityData();

  if (isPro && runId) {
    fetchCompetitive();
  } else {
    setCompetitiveData([]);
  }
}, [account, isPro, runId]);



  async function fetchAnalysis() {
  setStatus("loading");
  setStatusMessage("");

  const { data, error } = await supabase
    .from("ai_analyst_view")
    .select("*")
    .eq("account_id", account)
    .maybeSingle();

  if (error) {
    console.error("[AIAnalyst] Analyst query error", error);
    setData(null);
    setStatus("error");
    setStatusMessage("This module could not load right now.");
    return;
  }

  if (!data) {
    setData(null);
    setStatus("empty");
    setStatusMessage("Not enough data available yet.");
    return;
  }

  setData(data);
  setStatus("ready");
}
async function fetchJourneyData() {
  if (!account || !runId) {
    setJourneyData([]);
    return;
  }

  const { data, error } = await supabase
    .from("toren_competitive_radar_role_v1")
    .select("category_group, category_label, brand, mentions, prompt_count")
    .eq("account_id", account)
    .eq("run_id", runId);

  if (error) {
    console.error("[AIAnalyst] Journey query error", error);
    setJourneyData([]);
    return;
  }

  setJourneyData(data || []);
}

async function fetchOpportunityData() {
  if (!account || !runId) {
    setOpportunityData([]);
    return;
  }

  const { data, error } = await supabase
    .from("prompt_results")
    .select("brand_mentions, category, visibility_signal")
    .eq("account_id", account)
    .eq("run_id", runId)
    .neq("visibility_signal", "error")
    .limit(1000);

  if (error) {
    console.warn("[AIAnalyst] Lightweight opportunity query error", error);
    setOpportunityData([]);
    return;
  }

  const customerKey = normalizeBrandMentionKey(customerBrand);
  const opportunityMap = new Map<string, any>();

  (data || []).forEach((row: any) => {
    const category = row.category || "category_discovery";
    const rowRollup = aggregateBrandMentionsForFrequency(
      [row],
      (item: any) => item.brand_mentions,
      customerBrand
    );

    rowRollup.forEach((item) => {
      if (item.canonicalKey === customerKey) return;

      const existing = opportunityMap.get(item.canonicalKey);

      if (existing) {
        existing.total_gap_count += item.count;
        return;
      }

      opportunityMap.set(item.canonicalKey, {
        competitor: item.displayName,
        total_gap_count: item.count,
        primary_category: category,
        category_label: category.replace(/_/g, " "),
        impact_level: "Medium",
        recommended_action:
          "Strengthen competitive positioning and supporting content around this buyer-journey area."
      });
    });
  });

  setOpportunityData(
    Array.from(opportunityMap.values())
      .sort((a, b) => b.total_gap_count - a.total_gap_count)
      .slice(0, 3)
  );
}
  async function fetchCompetitive() {
  if (!account || !runId) {
    setCompetitiveData([]);
    return;
  }

  const { data, error } = await supabase
    .from("prompt_results")
    .select("brand_mentions, visibility_signal")
    .eq("account_id", account)
    .eq("run_id", runId)
    .neq("visibility_signal", "error")
    .limit(1000);

  if (error) {
    console.warn("[AIAnalyst] Lightweight competitive query error", error);
    setCompetitiveData([]);
    return;
  }

  const rollup = aggregateBrandMentionsForFrequency(
    data || [],
    (row: any) => row.brand_mentions,
    customerBrand
  );

  const totalMentions = rollup.reduce((sum, item) => sum + item.count, 0);

  setCompetitiveData(
    rollup
      .slice()
      .sort((a, b) => b.count - a.count)
      .slice(0, 12)
      .map((item, index) => ({
        brand: item.displayName,
        share_of_voice: totalMentions > 0 ? item.count / totalMentions : 0,
        weighted_mentions: item.count,
        rank: index + 1
      }))
  );
}

  function getInsight(d: AnalystData) {
    const key = account + JSON.stringify(d);

    if (d.discovery_mentions > d.recommendation_mentions * 2) {
      return pickStable(insightLibrary.discoveryVsRecommendation, key);
    }

    if (d.comparison_mentions > d.recommendation_mentions) {
      return pickStable(insightLibrary.comparisonPressure, key);
    }

    if (d.competitor_count > 5) {
      return pickStable(insightLibrary.competitiveMarket, key);
    }

    if (d.recommendation_mentions > d.discovery_mentions) {
      return pickStable(insightLibrary.strongPerformance, key);
    }

    return pickStable(insightLibrary.balanced, key);
  }

  function getTopCompetitor(data: any[]) {
    if (!data.length) return null;

    const grouped: Record<string, number> = {};

    data.forEach((row) => {
      grouped[row.brand] = (grouped[row.brand] || 0) + row.share_of_voice;
    });

    const sorted = Object.entries(grouped).sort((a, b) => b[1] - a[1]);
    return sorted[0]?.[0] || null;
  }

  function normalize(str: string) {
  return str?.toLowerCase().replace(/[^a-z0-9]/g, "").trim();
}

function getRecommendationGap(compData: any[], customerBrand: string) {
  if (!compData.length || !customerBrand) return null;

  const grouped: Record<string, number> = {};
  const brandMap: Record<string, string> = {};

  compData.forEach((row) => {
    const key = normalize(row.brand);
    grouped[key] = (grouped[key] || 0) + Number(row.share_of_voice || 0);
    brandMap[key] = row.brand;
  });

  const normalizedCustomer = normalize(customerBrand);
  const customerScore = grouped[normalizedCustomer] || 0;

 const competitors = Object.entries(grouped)
  .filter(([key]) => key !== normalizedCustomer)
  .sort((a, b) => b[1] - a[1]);

let topCompetitorKey;
let topCompetitorScore;

// Always get highest competitor first
const top = competitors[0];

// If YOU are leader
if (customerScore >= top?.[1]) {
  // Find closest competitor below you
  const closest = competitors
    .sort((a, b) => b[1] - a[1])
    .find(([_, score]) => score < customerScore);

  if (closest) {
    topCompetitorKey = closest[0];
    topCompetitorScore = closest[1];
  } else {
    // 🔥 FALLBACK: use top competitor anyway
    topCompetitorKey = top?.[0];
    topCompetitorScore = top?.[1];
  }
} else {
  // You are NOT leader → use top competitor
  topCompetitorKey = top?.[0];
  topCompetitorScore = top?.[1];
}

  if (!topCompetitorKey) {
    return {
      competitor: customerBrand,
      gap: 0,
      customerScore,
      competitorScore: customerScore,
      isLeader: true
    };
  }

  const gap = customerScore === 0
  ? 100
  : Math.round(((topCompetitorScore - customerScore) / customerScore) * 100);

  return {
    competitor: brandMap[topCompetitorKey] || topCompetitorKey,
    gap,
    customerScore,
    competitorScore: topCompetitorScore,
    isLeader: customerScore >= topCompetitorScore
  };
}
function getMomentum(current: number, previous: number) {
  if (previous === 0) return null;

  const change = ((current - previous) / previous) * 100;

  if (Math.abs(change) < 5) {
    return {
      label: "stable",
      value: Math.round(change)
    };
  }

  if (change > 0) {
    return {
      label: "improving",
      value: Math.round(change)
    };
  }

  return {
    label: "declining",
    value: Math.round(change)
  };
}

function getGapDirection(current: number, previous: number) {
  if (!previous) return null;

  const change = ((current - previous) / previous) * 100;

  if (Math.abs(change) < 5) {
    return {
      label: "stable",
      value: Math.round(change)
    };
  }

  if (change > 0) {
    return {
      label: "closing",
      value: Math.round(change)
    };
  }

  return {
    label: "widening",
    value: Math.round(change)
  };
}
function generateSparkline(current: number, previous: number) {
  if (!previous) return [];

  const step = (current - previous) / 4;

  return [
    previous,
    previous + step,
    previous + step * 2,
    previous + step * 3,
    current
  ];
}

function renderSparkline(values: number[]) {
  if (!values.length) return null;

  const max = Math.max(...values);

  return (
    <div style={{ display: "flex", alignItems: "flex-end", gap: 3, marginTop: 6 }}>
      {values.map((v, i) => (
        <div
          key={i}
          style={{
            width: 6,
            height: `${(v / max) * 20}px`,
            background: "#92400e",
            borderRadius: 2,
            opacity: 0.8
          }}
        />
      ))}
    </div>
  );
}
function classifyGap(gap: number) {
  if (gap < 15) return "tight";
  if (gap < 50) return "competitive";
  return "dominant";
}

function getBiggestOpportunity(d: AnalystData) {
  const { discovery_mentions, recommendation_mentions, comparison_mentions } = d;

  const values = [
    { key: "discovery", value: discovery_mentions },
    { key: "recommendation", value: recommendation_mentions },
    { key: "comparison", value: comparison_mentions }
  ];

  values.sort((a, b) => a.value - b.value);

  return values[0].key;
}
    function getRecommendedAction(weakness: string) {
  if (weakness === "recommendation") {
    return {
      text: "Strengthen how your brand is positioned in AI responses by improving authority signals, use cases, and outcome-driven messaging.",
      stage: "recommendation"
    };
  }

  if (weakness === "comparison") {
    return {
      text: "Improve differentiation in competitive queries by clearly articulating why your brand is preferred over alternatives.",
      stage: "comparison"
    };
  }

  if (weakness === "discovery") {
    return {
      text: "Increase visibility in early-stage queries by expanding content coverage across key topics and use cases.",
      stage: "discovery"
    };
  }

  return {
    text: "Continue strengthening your positioning across AI-generated responses.",
    stage: "discovery"
  };
}

function formatBrandName(brand?: string) {
  if (!brand) return "";

  const knownBrands: Record<string, string> = {
    forney: "Forney",
    "miller electric": "Miller Electric",
    "lincoln electric": "Lincoln Electric",
    esab: "ESAB",
    hobart: "Hobart"
  };

  const key = brand.toLowerCase().trim();

  if (knownBrands[key]) return knownBrands[key];

  return brand
    .split(" ")
    .map((word) =>
      word.length ? word.charAt(0).toUpperCase() + word.slice(1) : word
    )
    .join(" ");
}

function getJourneyStageInsight() {
  if (!journeyData.length || !customerBrand) return null;

  const customer = normalize(customerBrand);
  const stageCounts: Record<string, { label: string; count: number }> = {};

  journeyData.forEach((row: any) => {
    const rowBrand = normalize(row.brand);

    if (
      rowBrand !== customer &&
      !rowBrand.includes(customer) &&
      !customer.includes(rowBrand)
    ) {
      return;
    }

    const key = row.category_group;
    if (!key) return;

    if (!stageCounts[key]) {
      stageCounts[key] = {
        label: row.category_label || key,
        count: 0
      };
    }

    stageCounts[key].count += Number(row.prompt_count || row.mentions || 0);
  });

  const stages = Object.entries(stageCounts)
    .map(([key, value]) => ({
      key,
      label: value.label,
      count: value.count
    }))
    .sort((a, b) => b.count - a.count);

  if (!stages.length) {
    return {
      strongest: null,
      weakest: null,
      lead: "Visible, but not chosen.",
      body: `${formatBrandName(customerBrand)} is not consistently included across the latest organic buyer-journey prompts.`,
      action:
        "Start by strengthening category, comparison, and purchase-stage content so AI has clearer signals for when to include the brand.",
      stage: "category_discovery"
    };
  }

  const allStageOrder = [
    { key: "category_discovery", label: "Category Discovery" },
    { key: "alternatives_replacements", label: "Alternatives / Replacements" },
    { key: "comparisons", label: "Comparisons" },
    { key: "use_case_fit", label: "Use-Case Fit" },
    { key: "pain_point_led", label: "Pain Points" },
    { key: "purchase_shortlist", label: "Purchase Shortlist" }
  ];

  const byKey = Object.fromEntries(stages.map((stage) => [stage.key, stage]));
  const fullStages = allStageOrder.map((stage) => ({
    ...stage,
    count: byKey[stage.key]?.count || 0
  }));

  const strongest = [...fullStages].sort((a, b) => b.count - a.count)[0];
  const weakest = [...fullStages].sort((a, b) => a.count - b.count)[0];

  const topOpportunity = opportunityData?.[0];

  return {
    strongest,
    weakest,
    lead: `${formatBrandName(customerBrand)} is most visible in ${strongest.label}.`,
    body: topOpportunity
      ? `${formatBrandName(topOpportunity.competitor)} is creating the strongest competitive pressure in ${topOpportunity.category_label || topOpportunity.primary_category}.`
      : `${weakest.label} is the clearest buyer-journey gap based on the latest completed run.`,
    action:
      topOpportunity?.recommended_action ||
      `Strengthen ${weakest.label.toLowerCase()} content to help AI include the brand more consistently.`,
    stage: weakest.key
  };
}
function getProInsight(d: AnalystData) {
  const gapData = getRecommendationGap(competitiveData, customerBrand);
  if (!gapData) return null;

  const { competitor, gap, isLeader } = gapData;

  const gapType = classifyGap(gap);
  const opportunity = getBiggestOpportunity(d);
  const previous = d.previous_recommendation_mentions || d.recommendation_mentions;
const momentum = getMomentum(d.recommendation_mentions, previous);
  const actionData = getRecommendedAction(opportunity);
const learningLinks = getLearningLinks(actionData.stage);
  const sparkline = generateSparkline(d.recommendation_mentions, previous);
const gapDirection = getGapDirection(d.recommendation_mentions, previous);

  // 🟢 If you are winning
  if (isLeader) {
  const fallbackLink = getLearningLinks("recommendation");

  return {
    lead: `You are leading. ${competitor} is closest behind.`,
    body: "Your brand is currently the top performer in decision-stage queries.",
    action:
      "Maintain authority and continue strengthening competitive differentiation.",
    actionLink: fallbackLink, // ✅ ADD THIS
    opportunity,
    momentum,
    sparkline,
    gapDirection,
    competitorSparkline: sparkline,
    competitorName: competitor
  };
}

  // 🔴 If you have zero recommendation presence
  if (d.recommendation_mentions === 0) {
    const fallbackLink = getLearningLinks("recommendation"); 
    return {
      lead: `${competitor} dominates recommendation queries.`,
      body:
        "Your largest opportunity is improving positioning in recommendation-stage queries to increase selection.",
      action:
        "Improve positioning in high-intent prompts and strengthen brand authority signals.",
        actionLink: fallbackLink, // ✅ ADD THIS
    };
  }

  // 🎯 BUILD LEAD (gap-aware)
  let lead = `${competitor} is ${gap}% stronger in AI recommendation strength.`;

  if (gapType === "tight") {
    lead = `${competitor} is slightly ahead (${gap}%) in AI recommendation strength.`;
  } else if (gapType === "competitive") {
    lead = `${competitor} is pulling ahead (${gap}%) in AI recommendation strength.`;
  } else if (gapType === "dominant") {
    lead = `${competitor} is significantly outperforming you (${gap}%) in AI recommendation strength.`;
  }

  // 🎯 BUILD BODY (opportunity framing)
let body = `${competitor} is creating stronger competitive pressure across buyer-journey prompts.`;
  if (opportunity === "recommendation") {
    body =
  "Your largest opportunity is in recommendation-stage queries, where strengthening positioning can improve selection rates.";

if (momentum) {
  if (momentum.label === "improving") {
    body += ` You are improving (+${momentum.value}%), indicating positive momentum.`;
  } else if (momentum.label === "declining") {
    body += ` Performance is declining (${momentum.value}%), increasing urgency to act.`;
  }
}
  } else if (opportunity === "comparison") {
  body = `Your largest opportunity is in evaluation-stage prompts, where ${competitor} currently has stronger positioning.`;
} else if (opportunity === "discovery") {
  body =
    "Your largest opportunity is in early buyer-journey visibility, increasing how often your brand enters AI consideration sets.";
}
if (gapDirection?.label === "closing") {
  lead += " You are closing the gap.";
}

if (gapDirection?.label === "widening") {
  lead += " The gap is widening.";
}

if (gapDirection?.label === "stable") {
  lead += " The gap is stable.";
}
  // ✅ FINAL RETURN
 return {
  lead,
  body,
  action: actionData.text,
  actionLink: learningLinks,
  opportunity,
  momentum,
  sparkline,
  gapDirection,
  competitorSparkline: sparkline,
  competitorName: competitor
};
}


  if (status !== "ready" || !data) {
  return (
    <ModuleContainer
      title="AI Analyst"
      state={status}
      message={statusMessage}
    />
  );
}

  const journeyInsight = getJourneyStageInsight();
const insight = journeyInsight || getInsight(data);
const proInsight = isPro ? getProInsight(data) : null;
const coreOpportunity = journeyInsight?.stage || getBiggestOpportunity(data);
const coreLink = getLearningLinks(coreOpportunity);
const primaryCoreLink = coreLink?.[0];
const actionLink = proInsight?.actionLink?.[0];
const proActionPlan = proInsight
  ? getProActionPlan(proInsight, journeyInsight)
  : null;

function getProActionPlan(proInsight: any, journeyInsight: any) {
  const topOpportunity = opportunityData?.[0];

  const primaryThreat =
    topOpportunity?.competitor ||
    proInsight?.competitorName ||
    "Top competitor";

  const growthLever =
    journeyInsight?.weakest?.label ||
    topOpportunity?.category_label ||
    proInsight?.opportunity ||
    "Buyer Journey Visibility";

  const recommendedAction =
    topOpportunity?.recommended_action ||
    journeyInsight?.action ||
    proInsight?.action ||
    "Strengthen buyer-journey content so AI has clearer signals for when to include your brand.";

    const actionLink =
  journeyInsight?.stage
    ? getLearningLinks(journeyInsight.stage)?.[0]
    : proInsight?.actionLink?.[0];
    
  return {
    primaryThreat: formatBrandName(primaryThreat),
    growthLever,
    recommendedAction
  };
}
  return (

    
    <ModuleContainer title="AI Analyst">
      
      <p
        style={{
          marginBottom: 6,
          color: "#6b7280",
          fontSize: 13
        }}
      >
Interprets your AI visibility across the buyer journey and identifies where competitors are creating pressure.      </p>

      {/* CORE INSIGHT */}
      <div
        style={{
          marginTop: 6,
          padding: "10px 12px",
          background: "#f8fafc",
          border: "1px solid #e2e8f0",
          borderRadius: 10
        }}
      >
        <div style={{ fontSize: 11, fontWeight: 600, color: "#64748b", marginBottom: 4 }}>
          🧠 AI ANALYST
        </div>


        <div style={{ fontSize: 13, color: "#111827", lineHeight: 1.3 }}>
          <div style={{ fontWeight: 600 }}>{insight.lead}</div>
          <div style={{ opacity: 0.9 }}>{insight.body}</div>
          <div style={{ marginTop: 4, fontStyle: "italic", opacity: 0.8 }}>
            → {insight.action}{" "}
{primaryCoreLink && (
  <a
    href={primaryCoreLink.href}
    target={primaryCoreLink.external ? "_blank" : undefined}
    rel={primaryCoreLink.external ? "noreferrer" : undefined}
    style={{
      color: colors.primary,
      fontWeight: 500,
      textDecoration: "none"
    }}
  >
    {primaryCoreLink.label}
  </a>
)}
          </div>
        </div>
      </div>

      {/* PRO BLOCK */}
      {isPro && proInsight && (
        <div
          style={{
            marginTop: 10,
            padding: "10px 12px",
            background: "#fef3c7",
            border: "1px solid #fde68a",
            borderRadius: 10
          }}
        >
          <div style={{ fontSize: 11, fontWeight: 600, color: "#92400e", marginBottom: 4 }}>
             <span
  style={{
    fontSize: 10,
    fontWeight: 700,
    color: "#92400e",
    background: "#fde68a",       
    border: "1px solid #f59e0b",   
    borderRadius: 6,
    padding: "2px 6px",
    letterSpacing: "0.04em"
  }}
>  PRO
</span>  COMPETITIVE ACTION PLAN  
           
          </div>
          <div style={{ fontSize: 13, color: "#78350f", lineHeight: 1.3 }}>
            <div style={{ fontWeight: 700 }}>
  {proInsight.lead}
  
</div>
{proInsight.sparkline && (
  <div style={{ marginTop: 8 }}>
  
  <div style={{ fontSize: 11, opacity: 0.7, marginBottom: 2 }}>
    You
  </div>
  {renderSparkline(proInsight.sparkline)}

{proInsight.competitorSparkline && (
  <>
    <div style={{ fontSize: 11, opacity: 0.5, marginTop: 6, marginBottom: 2 }}>
      {proInsight.competitorName}
    </div>
    {renderSparkline(proInsight.competitorSparkline)}
  </>
)}

</div>
)}
{proInsight.gapDirection && (
  <div style={{ marginTop: 6, fontSize: 12, opacity: 0.75 }}>
    Trend:{" "}
    {proInsight.gapDirection.label === "closing" && "⬆️ improving"}
    {proInsight.gapDirection.label === "widening" && "⬇️ declining"}
    {proInsight.gapDirection.label === "stable" && "→ stable"}
    {" "}({proInsight.gapDirection.value > 0 ? "+" : ""}
    {proInsight.gapDirection.value}%)
  </div>
)}
            <div style={{ marginTop: 4 }}>
  {journeyInsight?.body || proInsight.body}
</div>

{proActionPlan && (
  <div
    style={{
      marginTop: 10,
      padding: "10px 12px",
      background: "rgba(255,255,255,0.45)",
      border: "1px solid rgba(120,53,15,0.16)",
      borderRadius: 10
    }}
  >
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
        gap: 10
      }}
    >
      <div>
        <div
          style={{
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: "0.08em",
            textTransform: "uppercase",
            opacity: 0.65
          }}
        >
          Primary threat
        </div>
        <div style={{ marginTop: 3, fontWeight: 750 }}>
          {proActionPlan.primaryThreat}
        </div>
      </div>

      <div>
        <div
          style={{
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: "0.08em",
            textTransform: "uppercase",
            opacity: 0.65
          }}
        >
          Growth lever
        </div>
        <div style={{ marginTop: 3, fontWeight: 750 }}>
          {proActionPlan.growthLever}
        </div>
      </div>

      <div>
        <div
          style={{
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: "0.08em",
            textTransform: "uppercase",
            opacity: 0.65
          }}
        >
          Recommended action
        </div>
        <div style={{ marginTop: 3, fontWeight: 750 }}>
          Build stage-specific content
        </div>
      </div>
    </div>

    <div
      style={{
        marginTop: 10,
        paddingTop: 8,
        borderTop: "1px solid rgba(120,53,15,0.12)",
        fontSize: 13,
        lineHeight: 1.45
      }}
    >
      {proActionPlan.recommendedAction}
    </div>
  </div>
)}

<div
  style={{
    marginTop: 8,
    paddingTop: 6,
    borderTop: "1px solid rgba(120,53,15,0.15)",
    fontWeight: 600
  }}
>
  Largest opportunity:{" "}
  {journeyInsight?.weakest?.label ||
    proActionPlan?.growthLever ||
    "Buyer Journey Visibility"}
</div>

<div style={{ marginTop: 6, fontStyle: "italic", opacity: 0.95 }}>
  →{" "}
  {journeyInsight?.action ||
    proActionPlan?.recommendedAction ||
    proInsight.action}{" "}
  {actionLink && (
    <a
      href={actionLink.href}
      target={actionLink.external ? "_blank" : undefined}
      rel={actionLink.external ? "noreferrer" : undefined}
      style={{
        color: colors.primary,
        fontWeight: 500,
        textDecoration: "none"
      }}
    >
      {actionLink.label}
    </a>
  )}
</div>

          </div>
        </div>
        
      )}

      {/* UPSELL */}
      {!isPro && (
  <div
    style={{
      marginTop: 10,
      padding: "10px 12px",
      background: "#f1f5f9",
      border: "1px dashed #cbd5f5",
      borderRadius: 10
    }}
  >
    <div style={{ fontSize: 12, color: "#475569" }}>
      ⚡ Unlock competitive insights — see who is outperforming you in AI responses.
    </div>

    <div style={{ marginTop: 6 }}>
      <a
        href="mailto:sales@torenindex.com"
        style={{
          fontSize: 12,
          color: colors.primary,
          fontWeight: 500,
          textDecoration: "none"
        }}
      >
        Contact sales →
      </a>
    </div>
  </div>
)}

    </ModuleContainer>
  );
}
